// src/components/fraudMatch/FraudAlertsPanel.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningIcon from "@mui/icons-material/Warning";
import ShieldIcon from "@mui/icons-material/Shield";
import BlockIcon from "@mui/icons-material/Block";
import Divider from "@mui/material/Divider";
import apiClient from "../../services/EnhancedApiClient";
import { useEnvironment } from "../../context/EnvironmentContext";

const FraudAlertsPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [displayMode, setDisplayMode] = useState("all");
  const [refreshing, setRefreshing] = useState(false);
  const [chargebackBlocks, setChargebackBlocks] = useState([]);
  const [chargebackLoading, setChargebackLoading] = useState(true);
  const [chargebackError, setChargebackError] = useState(null);
  const [chargebackPage, setChargebackPage] = useState(0);
  const [chargebackRowsPerPage, setChargebackRowsPerPage] = useState(10);
  const [chargebackTotal, setChargebackTotal] = useState(0);
  const [chargebackLastUpdated, setChargebackLastUpdated] = useState(null);
  const [chargebackRefreshing, setChargebackRefreshing] = useState(false);
  const { environment } = useEnvironment();

  const fetchAlerts = useCallback(
    async (force = false) => {
      try {
        if (force) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        // Prepare query parameters
        const options = {
          limit: rowsPerPage,
          page: page + 1, // API uses 1-based pagination
          force,
        };

        if (lastUpdated) {
          options.since = lastUpdated.getTime();
        }

        const response = await apiClient.fetchFraudAlerts(options);

        setAlerts(response.alerts || []);
        console.log("Fraud alerts data:", response.alerts); // ADD THIS LINE
        console.log("Display mode:", displayMode); // ADD THIS LINE
        setTotalCount(response.totalCount || 0);
        setLastUpdated(new Date(response.latestUpdate));
        setError(null);
      } catch (err) {
        console.error("Error fetching fraud alerts:", err);
        setError("Failed to load fraud alerts");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, rowsPerPage, environment]
  ); // Include dependencies that fetchAlerts uses

  const fetchChargebackBlocks = useCallback(
    async (force = false) => {
      try {
        if (force) {
          setChargebackRefreshing(true);
        } else {
          setChargebackLoading(true);
        }
        const options = {
          limit: chargebackRowsPerPage,
          page: chargebackPage + 1,
          force,
        };
        const response = await apiClient.fetchChargebackBlocks(options);
        setChargebackBlocks(response.blocks || []);
        setChargebackTotal(response.totalCount || 0);
        setChargebackLastUpdated(new Date(response.latestUpdate));
        setChargebackError(null);
      } catch (err) {
        console.error("Error fetching chargeback blocks:", err);
        setChargebackError("Failed to load chargeback blocks");
      } finally {
        setChargebackLoading(false);
        setChargebackRefreshing(false);
      }
    },
    [chargebackPage, chargebackRowsPerPage, environment]
  );

  useEffect(() => {
    fetchAlerts();
  }, [environment, fetchAlerts]);

  useEffect(() => {
    fetchChargebackBlocks();
  }, [environment, fetchChargebackBlocks]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchAlerts(true);
  };

  const handleDisplayModeChange = (event) => {
    setDisplayMode(event.target.value);
    setPage(0);
  };

  // Filter alerts based on display mode only (time filtering removed)
  const getFilteredAlerts = () => {
    if (!alerts || alerts.length === 0) return [];

    console.log(
      "Filtering alerts - Display mode:",
      displayMode,
      "Alerts:",
      alerts
    );

    // Filter by display mode only
    let filtered = alerts;
    if (displayMode === "shadow") {
      filtered = alerts.filter(
        (alert) =>
          alert.type === "shadow_fraud_detected" || alert.shadowMode === true
      );
    } else if (displayMode === "production") {
      filtered = alerts.filter(
        (alert) => alert.type === "fraud_detected" && alert.shadowMode !== true
      );
    }

    // Time filtering removed - always show all alerts regardless of time

    return filtered;
  };

  const filteredAlerts = getFilteredAlerts();

  // Add this sorting code to display newest alerts first
  filteredAlerts.sort((a, b) => {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);
    return dateB - dateA; // Descending order (newest first)
  });

  return (
    <Paper
      id="panel-fraud-alerts"
      sx={{
        p: 0,
        mb: 0,
        borderTop: "4px solid #d97706",
        boxShadow:
          "0 -2px 4px rgba(217, 119, 6, 0.1), 0 2px 8px rgba(0,0,0,0.1)",
        overflow: "hidden",
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }}
    >
      {/* Header Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor: "#fff7ed",
          padding: "20px 24px 16px 24px",
          mb: 0,
        }}
      >
        <Typography variant="h5" component="h2">
          <Box display="flex" alignItems="center" gap={1}>
            <BlockIcon sx={{ fontSize: "1.5em", color: "#d97706" }} />
            <Box>
              <Box component="span" fontWeight="bold">
                Fraud Matching Blocks
                {environment === "staging" && (
                  <Chip
                    label="STAGING"
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Typography>
        <Box display="flex" alignItems="center">
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
              Updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </Box>
      <Typography sx={{ fontSize: '1rem', fontWeight: 'normal', px: 3, pb: 1, backgroundColor: '#fff7ed' }} color="text.secondary">
        Auto blocks users who match against Fraud DB with a risk score &gt;= 50. Block persists while match exists in Fraud DB.
      </Typography>



      {/* Main Content Section - White Background */}
      <Box sx={{ px: 3, pb: 3, pt: 2, backgroundColor: "white" }}>
        {loading && !refreshing && !alerts.length ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Showing {filteredAlerts.length} alerts
                {totalCount > filteredAlerts.length &&
                  ` (of ${totalCount} total)`}
              </Typography>

              <Box>
                <Chip
                  icon={<ShieldIcon />}
                  label="Shadow Mode"
                  size="small"
                  color="warning"
                  sx={{ mr: 1 }}
                />
                <Chip
                  icon={<WarningIcon />}
                  label="Production"
                  size="small"
                  color="error"
                />
              </Box>
            </Box>

            <TableContainer
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              {" "}
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Timestamp
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Email
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      IP Address
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Risk Score
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Reasons
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}
                    >
                      Mode
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAlerts.length > 0 ? (
                    filteredAlerts.map((alert, index) => (
                      <TableRow
                        key={
                          alert.uniqueId ||
                          (alert.timestamp && alert.email
                            ? `${alert.timestamp}-${alert.email}`
                            : `row-${index}`)
                        }
                        hover
                        sx={{
                          bgcolor: alert.shadowMode
                            ? "rgba(237, 108, 2, 0.05)"
                            : "rgba(211, 47, 47, 0.05)",
                        }}
                      >
                        <TableCell>
                          {new Date(alert.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {[alert.firstName, alert.lastName]
                            .filter(Boolean)
                            .join(" ") || "Unknown"}
                        </TableCell>
                        <TableCell>{alert.email || "Unknown"}</TableCell>
                        <TableCell>
                          <code>{alert.ipAddress || "Unknown"}</code>
                        </TableCell>
                        <TableCell>
                          {alert.amount ||
                          alert.orderTotal ||
                          alert.transactionAmount ? (
                            <Box display="flex" alignItems="center">
                              <Typography variant="body2">
                                $
                                {(
                                  alert.amount ||
                                  alert.orderTotal ||
                                  alert.transactionAmount ||
                                  0
                                ).toFixed(2)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={alert.riskScore || "0"}
                            size="small"
                            color={
                              alert.riskScore > 70
                                ? "error"
                                : alert.riskScore > 40
                                ? "warning"
                                : "success"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {alert.reasons && alert.reasons.length > 0 ? (
                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                              {alert.reasons.map((reason, idx) => (
                                <li key={idx}>{reason}</li>
                              ))}
                            </ul>
                          ) : (
                            "Unknown"
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              alert.shadowMode ? "Shadow Mode" : "Production"
                            }
                          >
                            {alert.shadowMode ? (
                              <ShieldIcon color="warning" fontSize="small" />
                            ) : (
                              <WarningIcon color="error" fontSize="small" />
                            )}
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No fraud alerts found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Box>

      {/* Chargeback Blocks Section */}
      <Divider />
      <Box
        id="panel-chargeback-blocks"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          backgroundColor: "#fff7ed",
          borderTop: "4px solid #d97706",
          padding: "20px 24px 16px 24px",
          mb: 0,
        }}
      >
        <Typography variant="h5" component="h2">
          <Box display="flex" alignItems="center" gap={1}>
            <BlockIcon sx={{ fontSize: "1.5em", color: "#d97706" }} />
            <Box>
              <Box component="span" fontWeight="bold">
                Chargeback Blocks
              </Box>
            </Box>
          </Box>
        </Typography>
        <Box display="flex" alignItems="center">
          {chargebackLastUpdated && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
              Updated: {chargebackLastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchChargebackBlocks(true)}
            disabled={chargebackRefreshing}
            size="small"
          >
            {chargebackRefreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </Box>
      <Typography sx={{ fontSize: '1rem', fontWeight: 'normal', px: 3, pb: 1, backgroundColor: '#fff7ed' }} color="text.secondary">
        Auto blocks users forever who match email, billing address, shipping address or phone number against Chargeback list.
      </Typography>

      <Box sx={{ px: 3, pb: 3, pt: 2, backgroundColor: "white" }}>
        {chargebackLoading && !chargebackRefreshing && !chargebackBlocks.length ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : chargebackError ? (
          <Alert severity="error" sx={{ my: 2 }}>
            {chargebackError}
          </Alert>
        ) : (
          <>
            <TableContainer
              sx={{
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9fafb" }}>
                    <TableCell sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}>
                      Timestamp
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}>
                      Order Number
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}>
                      Customer Name
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}>
                      Match Type
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}>
                      Billing Address
                    </TableCell>
                    <TableCell sx={{ backgroundColor: "#f9fafb", fontWeight: 600 }}>
                      Amount
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {chargebackBlocks.length > 0 ? (
                    chargebackBlocks.map((block, index) => (
                      <TableRow
                        key={block.id || `cb-row-${index}`}
                        hover
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(217, 119, 6, 0.05) !important",
                          },
                        }}
                      >
                        <TableCell>
                          {new Date(block.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>{block.orderNumber}</TableCell>
                        <TableCell>{block.custName}</TableCell>
                        <TableCell>{block.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              block.matchType === "email"
                                ? "Email"
                                : block.matchType === "name"
                                ? "Name"
                                : block.matchType === "billingAddress"
                                ? "Billing Addr"
                                : block.matchType === "shippingAddress"
                                ? "Ship Addr"
                                : block.matchType
                            }
                            size="small"
                            color={
                              block.matchType === "email"
                                ? "error"
                                : block.matchType === "name" ||
                                  block.matchType === "billingAddress" ||
                                  block.matchType === "shippingAddress"
                                ? "warning"
                                : "default"
                            }
                          />
                        </TableCell>
                        <TableCell>{block.billingAddress}</TableCell>
                        <TableCell>
                          {block.amount ? (
                            <Typography variant="body2">
                              ${block.amount.toFixed(2)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No chargeback blocks found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={chargebackTotal}
              rowsPerPage={chargebackRowsPerPage}
              page={chargebackPage}
              onPageChange={(event, newPage) => setChargebackPage(newPage)}
              onRowsPerPageChange={(event) => {
                setChargebackRowsPerPage(parseInt(event.target.value, 10));
                setChargebackPage(0);
              }}
            />
          </>
        )}
      </Box>
    </Paper>
  );
};

export default FraudAlertsPanel;